import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, GraduationCap, Award, Users, Search, 
  Loader2, CheckCircle, XCircle, Calendar, Mail, 
  DollarSign, Eye, MoreVertical, UserCheck, Clock
} from 'lucide-react';
import { motion } from 'framer-motion';
import { adminFetch, isAdminAuthenticated } from '@/utils/adminAuth';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminMemberships = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('subscribers');
  const [subscribers, setSubscribers] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (!isAdminAuthenticated()) {
      navigate('/admin');
      return;
    }
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [subsRes, enrollRes, certsRes] = await Promise.all([
        adminFetch('/admin/memberships'),
        adminFetch('/admin/enrollments'),
        adminFetch('/admin/certificates')
      ]);
      
      if (subsRes.ok) setSubscribers(await subsRes.json());
      if (enrollRes.ok) setEnrollments(await enrollRes.json());
      if (certsRes.ok) setCertificates(await certsRes.json());
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelMembership = async (membershipId) => {
    if (!window.confirm('Are you sure you want to cancel this membership?')) return;
    
    try {
      const response = await adminFetch(`/admin/memberships/${membershipId}/cancel`, {
        method: 'PUT'
      });
      if (response.ok) {
        toast.success('Membership cancelled');
        loadData();
      }
    } catch (error) {
      toast.error('Failed to cancel membership');
    }
  };

  const handleIssueCertificate = async (enrollmentId) => {
    try {
      const response = await adminFetch(`/courses/complete/${enrollmentId}`, {
        method: 'POST'
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(`Certificate ${data.certificate_id} issued!`);
        loadData();
      }
    } catch (error) {
      toast.error('Failed to issue certificate');
    }
  };

  const filteredData = () => {
    const term = searchTerm.toLowerCase();
    if (activeTab === 'subscribers') {
      return subscribers.filter(s => 
        s.user_email?.toLowerCase().includes(term) ||
        s.user_name?.toLowerCase().includes(term) ||
        s.plan_name?.toLowerCase().includes(term)
      );
    } else if (activeTab === 'enrollments') {
      return enrollments.filter(e => 
        e.student_email?.toLowerCase().includes(term) ||
        e.student_name?.toLowerCase().includes(term) ||
        e.course_name?.toLowerCase().includes(term)
      );
    } else {
      return certificates.filter(c => 
        c.student_email?.toLowerCase().includes(term) ||
        c.student_name?.toLowerCase().includes(term) ||
        c.course_name?.toLowerCase().includes(term) ||
        c.id?.toLowerCase().includes(term)
      );
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/admin/dashboard')}
                className="p-2 hover:bg-card rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold">Memberships & Courses</h1>
                <p className="text-sm text-muted-foreground">Manage subscribers, enrollments, and certificates</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-card border border-border rounded-2xl p-6"
          >
            <Users className="w-8 h-8 text-primary mb-3" />
            <p className="text-3xl font-bold">{subscribers.length}</p>
            <p className="text-muted-foreground">Active Subscribers</p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-card border border-border rounded-2xl p-6"
          >
            <GraduationCap className="w-8 h-8 text-green-500 mb-3" />
            <p className="text-3xl font-bold">{enrollments.length}</p>
            <p className="text-muted-foreground">Course Enrollments</p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-card border border-border rounded-2xl p-6"
          >
            <Award className="w-8 h-8 text-amber-500 mb-3" />
            <p className="text-3xl font-bold">{certificates.length}</p>
            <p className="text-muted-foreground">Certificates Issued</p>
          </motion.div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {[
            { id: 'subscribers', label: 'Subscribers', icon: Users },
            { id: 'enrollments', label: 'Course Enrollments', icon: GraduationCap },
            { id: 'certificates', label: 'Certificates', icon: Award },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                activeTab === tab.id 
                  ? 'bg-primary text-primary-foreground' 
                  : 'bg-card hover:bg-card/80 text-foreground'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by name, email, or course..."
            className="w-full pl-12 pr-4 py-3 bg-card border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        {/* Content */}
        <div className="bg-card border border-border rounded-2xl overflow-hidden">
          {activeTab === 'subscribers' && (
            <table className="w-full">
              <thead className="bg-background/50">
                <tr>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-muted-foreground">Member</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-muted-foreground">Plan</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-muted-foreground">Started</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-muted-foreground">Status</th>
                  <th className="text-right px-6 py-4 text-sm font-semibold text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filteredData().length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center text-muted-foreground">
                      No subscribers found
                    </td>
                  </tr>
                ) : (
                  filteredData().map((sub) => (
                    <tr key={sub.id} className="hover:bg-background/50">
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-medium">{sub.user_name || 'Unknown'}</p>
                          <p className="text-sm text-muted-foreground">{sub.user_email}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                          sub.plan_name?.includes('Gold') ? 'bg-amber-500/20 text-amber-500' :
                          sub.plan_name?.includes('Silver') ? 'bg-gray-500/20 text-gray-400' :
                          'bg-primary/20 text-primary'
                        }`}>
                          {sub.plan_name || sub.plan_id}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-muted-foreground">
                        {formatDate(sub.started_at)}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`flex items-center gap-1 text-sm ${
                          sub.status === 'active' ? 'text-green-500' : 'text-red-500'
                        }`}>
                          {sub.status === 'active' ? (
                            <CheckCircle className="w-4 h-4" />
                          ) : (
                            <XCircle className="w-4 h-4" />
                          )}
                          {sub.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        {sub.status === 'active' && (
                          <button
                            onClick={() => handleCancelMembership(sub.id)}
                            className="text-sm text-red-500 hover:underline"
                          >
                            Cancel
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}

          {activeTab === 'enrollments' && (
            <table className="w-full">
              <thead className="bg-background/50">
                <tr>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-muted-foreground">Student</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-muted-foreground">Course</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-muted-foreground">Progress</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-muted-foreground">Enrolled</th>
                  <th className="text-right px-6 py-4 text-sm font-semibold text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filteredData().length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center text-muted-foreground">
                      No enrollments found
                    </td>
                  </tr>
                ) : (
                  filteredData().map((enroll) => (
                    <tr key={enroll.id} className="hover:bg-background/50">
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-medium">{enroll.student_name || 'Unknown'}</p>
                          <p className="text-sm text-muted-foreground">{enroll.student_email}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="font-medium">{enroll.course_name}</p>
                        <p className="text-sm text-muted-foreground">${enroll.price}</p>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-24 h-2 bg-background rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-primary rounded-full"
                              style={{ width: `${enroll.progress || 0}%` }}
                            />
                          </div>
                          <span className="text-sm">{enroll.progress || 0}%</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-muted-foreground">
                        {formatDate(enroll.enrolled_at)}
                      </td>
                      <td className="px-6 py-4 text-right">
                        {!enroll.certificate_issued && (
                          <button
                            onClick={() => handleIssueCertificate(enroll.id)}
                            className="flex items-center gap-1 text-sm text-primary hover:underline ml-auto"
                          >
                            <Award className="w-4 h-4" />
                            Issue Certificate
                          </button>
                        )}
                        {enroll.certificate_issued && (
                          <span className="text-sm text-green-500 flex items-center gap-1 justify-end">
                            <CheckCircle className="w-4 h-4" />
                            Certified
                          </span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}

          {activeTab === 'certificates' && (
            <table className="w-full">
              <thead className="bg-background/50">
                <tr>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-muted-foreground">Certificate ID</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-muted-foreground">Recipient</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-muted-foreground">Course</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-muted-foreground">Issued</th>
                  <th className="text-right px-6 py-4 text-sm font-semibold text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filteredData().length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center text-muted-foreground">
                      No certificates found
                    </td>
                  </tr>
                ) : (
                  filteredData().map((cert) => (
                    <tr key={cert.id} className="hover:bg-background/50">
                      <td className="px-6 py-4">
                        <span className="font-mono bg-primary/10 text-primary px-2 py-1 rounded">
                          {cert.id}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-medium">{cert.student_name || 'Unknown'}</p>
                          <p className="text-sm text-muted-foreground">{cert.student_email}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {cert.course_name}
                      </td>
                      <td className="px-6 py-4 text-muted-foreground">
                        {formatDate(cert.issued_at)}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <a
                          href={`/certificates/verify/${cert.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-primary hover:underline flex items-center gap-1 justify-end"
                        >
                          <Eye className="w-4 h-4" />
                          View
                        </a>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminMemberships;
